import { DAILY_SECONDS, DEFAULT_COOKIE_EXPIRY_DAYS } from '../consts.mjs';
import { SameSiteProperties } from '../interfaces.mjs';

// © Sitecore Corporation A/S. All rights reserved. Sitecore® is a registered trademark of Sitecore Corporation A/S.
/**
 * Retrieves the cookie, if it exists in the cookie string
 * @param cookieStr - The cookie string containing every cookie
 * @param cookieName - The cookie name to be found
 * @returns - an object that contains the cookie name and value or undefined, if not found
 */
function getCookie(cookieStr, cookieName) {
    if (!cookieStr)
        return undefined;
    const found = cookieStr.split('; ').find((cookie) => {
        return cookie.indexOf('=') > 0 && cookie.split('=')[0] === cookieName;
    });
    return found !== undefined ? { name: found.split('=')[0], value: found.split('=')[1] } : undefined;
}
/**
 * Checks if the cookie exists in the cookie string
 * @param cookieStr - The cookie string containing every cookie
 * @param cookieName - The cookie name to be found
 * @returns - boolean value, if the cookie is found in the cookie string
 */
function cookieExists(cookieStr, cookieName) {
    return cookieStr.split('; ').some((cookie) => cookie.split('=')[0] === cookieName);
}
/**
 * Creates the cookie string with the respectively cookie attributes
 * @param name - name of the cookie
 * @param value - value of the cookie
 * @param attributes - an object of supported cookie attributes
 * @returns - a string that will be passed to document.cookie
 */
function createCookieString(name, value, attributes) {
    let cookieString = `${name}=${value};`;
    cookieString += ` Max-Age=${attributes.maxAge}; SameSite=${attributes.sameSite};`;
    cookieString += attributes.secure ? ' Secure;' : '';
    cookieString += attributes.path ? ` Path=${attributes.path};` : '';
    cookieString += attributes.domain ? ` Domain=${attributes.domain};` : '';
    cookieString = cookieString.substring(0, cookieString.length - 1);
    return cookieString;
}
/**
 * Gets the default Cookie Attributes
 * @param  maxAge - Set the cookie "Max-Age" attribute in days.
 * @returns the default configuration settings for the cookie string
 */
// eslint-disable-next-line max-len
function getDefaultCookieAttributes(maxAge = DEFAULT_COOKIE_EXPIRY_DAYS, cookieDomain) {
    return {
        domain: cookieDomain,
        maxAge: maxAge * DAILY_SECONDS,
        path: '/',
        sameSite: SameSiteProperties.None,
        secure: true,
    };
}
/**
 * Filters cookies starting with the given prefix
 * @param cookieStr - The cookie string containing every cookie
 * @param prefix - The prefix that will be used to filter the results
 * @returns - an array with cookie objects (name, value)
 */
function filterCookiesByPrefix(cookieStr, prefix) {
    return cookieStr
        .split('; ')
        .filter((cookie) => cookie.split('=')[0].startsWith(prefix))
        .map((cookie) => {
        return {
            name: cookie.split('=')[0],
            value: cookie.split('=')[1],
        };
    });
}
/**
 * Deletes a cookie from the document (client-side only)
 * @param cookieName - The cookie to be deleted
 */
function deleteCookie(cookieName) {
    document.cookie = cookieName + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
}

export { cookieExists, createCookieString, deleteCookie, filterCookiesByPrefix, getCookie, getDefaultCookieAttributes };
